/**
 * Clipboard serialization utilities for FlowDesigner copy/paste.
 *
 * @module clipboard-utils
 */
import type { WorkflowEdge } from '../../../types/workflow';

/** Keys stripped from node.data during serialization (non-serializable handlers / runtime state) */
const NON_SERIALIZABLE_KEYS = new Set([
  'onConfigClick',
  'onCreateNavigate',
  'onCreateAction',
  'onCreateAssertion',
  'onCreateWait',
  'onCreateLogin',
  'onCreateCondition',
  'onCreateApiSequence',
  'onCreateNotification',
  'onCreateYesBranchNode',
  'onCreateNoBranchNode',
  'onCacheClick',
  'onPlaybookClick',
  'playbookStepCount',
  'executionStatus',
  'executionMessage',
  'isExecuting',
  'hasCachedReplay',
  'cachedAt',
  'cacheRequiresAgent',
]);

/** Strip non-serializable keys (handlers, runtime state) from node data. */
export function serializeNodeData(data: Record<string, unknown>): Record<string, unknown> {
  const serialized: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(data)) {
    if (NON_SERIALIZABLE_KEYS.has(key)) continue;
    if (typeof value === 'function') continue;
    serialized[key] = value;
  }
  return serialized;
}

/** Versioned envelope written to the system clipboard. */
export interface ClipboardPayload {
  version: 1;
  source: 'chatsdet-flow-designer';
  nodes: Array<{
    originalId: string;
    type: string;
    data: Record<string, unknown>;
  }>;
  internalEdges: Array<{
    originalSource: string;
    originalTarget: string;
    edgeType: string;
  }>;
}

/** Type guard for clipboard payloads read back from JSON. */
export function isValidClipboardPayload(val: unknown): val is ClipboardPayload {
  if (typeof val !== 'object' || val === null) return false;
  const obj = val as Record<string, unknown>;
  return (
    obj.version === 1 &&
    obj.source === 'chatsdet-flow-designer' &&
    Array.isArray(obj.nodes) &&
    Array.isArray(obj.internalEdges)
  );
}

/**
 * Topologically sort a subset of node IDs based on edges.
 * Returns nodes in dependency order (roots first).
 */
export function topologicalSort(nodeIds: string[], edges: WorkflowEdge[]): string[] {
  const nodeSet = new Set(nodeIds);

  // Build adjacency within the selected set
  const inDegree = new Map<string, number>();
  const children = new Map<string, string[]>();
  for (const id of nodeIds) {
    inDegree.set(id, 0);
    children.set(id, []);
  }
  for (const edge of edges) {
    if (nodeSet.has(edge.source) && nodeSet.has(edge.target)) {
      children.get(edge.source)!.push(edge.target);
      inDegree.set(edge.target, (inDegree.get(edge.target) ?? 0) + 1);
    }
  }

  // BFS from roots
  const queue: string[] = [];
  for (const [id, deg] of inDegree) {
    if (deg === 0) queue.push(id);
  }

  const sorted: string[] = [];
  while (queue.length > 0) {
    const id = queue.shift()!;
    sorted.push(id);
    for (const child of children.get(id) ?? []) {
      const newDeg = (inDegree.get(child) ?? 1) - 1;
      inDegree.set(child, newDeg);
      if (newDeg === 0) queue.push(child);
    }
  }

  // Include any remaining (cycles or disconnected) at the end
  for (const id of nodeIds) {
    if (!sorted.includes(id)) sorted.push(id);
  }

  return sorted;
}
